import { useEffect } from 'react';
import { Route, Redirect, RouteProps } from 'react-router-dom';
import {
    UserRole,
    getPrimaryRole,
    isUserAdmin,
    isOrganizationAdministrator,
    isOrganizationRole,
    roleMatches,
} from 'src/domain/user';
import { useSignedInUser } from '../hooks/useSignedInUser';
import { useNotification } from 'src/components/Notification/useNotfication';
import { WrapIf } from 'src/components/WrapIf';
import { useHomePath } from '../hooks/useHomePath';
import { useTranslation } from 'react-i18next';

const useIsAuthenticated = () => {
    const user = useSignedInUser();
    return { isAuthenticated: Boolean(user) };
};

const useIsAuthorized = (
    roles: UserRole[] | undefined,
    {
        allowAdmin = false,
        adminOnly = false,
    }: { allowAdmin?: boolean; adminOnly?: boolean } = {},
) => {
    const user = useSignedInUser();
    const role = getPrimaryRole(user);
    const isAdmin = isUserAdmin(user);

    if (!user) {
        return { role, isAuthorized: false };
    }

    if (adminOnly) {
        return { role, isAuthorized: isAdmin };
    }

    if (!roles || roles.length === 0) {
        return { role, isAuthorized: allowAdmin ? isAdmin : true };
    }

    let isAuthorized = role
        ? roles.some(requiredRole => roleMatches(role, requiredRole))
        : false;
    if (
        !isAuthorized &&
        roles.some(requiredRole => isOrganizationRole(requiredRole))
    ) {
        isAuthorized = isOrganizationAdministrator(user);
    }
    if (!isAuthorized && allowAdmin) {
        isAuthorized = isAdmin;
    }

    return { role, isAuthorized };
};

type Props = {
    roles?: UserRole[];
    requireAuth?: boolean;
    allowAdmin?: boolean;
    adminOnly?: boolean;
} & RouteProps<string>;

const RequireAuth = ({ children }: { children: React.ReactNode }) => {
    const { isAuthenticated } = useIsAuthenticated();
    const notify = useNotification();
    const { t } = useTranslation();
    useEffect(() => {
        if (!isAuthenticated) {
            notify(t('notifications.authRequired'), true);
        }
    }, [notify, isAuthenticated, t]);
    return !isAuthenticated ? <Redirect to="/login" /> : <>{children}</>;
};
const RequireRole = ({
    roles,
    allowAdmin,
    adminOnly,
    children,
}: {
    roles?: UserRole[];
    allowAdmin?: boolean;
    adminOnly?: boolean;
    children: React.ReactNode;
}) => {
    const home = useHomePath();
    const { isAuthorized } = useIsAuthorized(roles, {
        allowAdmin,
        adminOnly,
    });
    const notify = useNotification();
    const { t } = useTranslation();
    useEffect(() => {
        if (!isAuthorized) {
            notify(t('notifications.accessDenied'), true);
        }
    }, [notify, isAuthorized, t]);

    return !isAuthorized ? <Redirect to={home} /> : <>{children}</>;
};

const AppRoute = ({
    component: Component,
    roles = [],
    requireAuth = false,
    allowAdmin = false,
    adminOnly = false,
    ...rest
}: Props) => {
    const shouldCheckRole = adminOnly || roles.length > 0;
    return (
        <Route
            {...rest}
            render={props => (
                <WrapIf
                    condition={requireAuth}
                    wrapper={children => <RequireAuth>{children}</RequireAuth>}
                >
                    <WrapIf
                        condition={shouldCheckRole}
                        wrapper={children => (
                            <RequireRole
                                roles={roles}
                                allowAdmin={allowAdmin}
                                adminOnly={adminOnly}
                            >
                                {children}
                            </RequireRole>
                        )}
                    >
                        {Component ? <Component {...props} /> : null}
                    </WrapIf>
                </WrapIf>
            )}
        />
    );
};

export default AppRoute;
